import type { Theme } from "@api/types";
import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import type { UserPreferences } from "../types/preferences";
import { useUserPreferences } from "./useUserPreferences";

/**
 * Hook that manages theme with database persistence for authenticated users
 */
export function useThemeManager() {
    const { user } = useAuth();
    const { setTheme, toggleTheme: toggleLocal } = useTheme();
    const { preferences, updatePreferences } = useUserPreferences();

    // Sync theme from user preferences when they load
    useEffect(() => {
        if (preferences?.display?.theme && user) {
            setTheme(preferences.display.theme);
        }
    }, [preferences?.display?.theme, user, setTheme]);

    // Database-aware theme toggle
    const toggleTheme = () => {
        if (user && preferences) {
            // For authenticated users, toggle and save to database
            const currentTheme = preferences.display.theme;
            const newTheme: Theme = currentTheme === "light" ? "dark" : "light";

            // Update theme context immediately
            setTheme(newTheme);

            // Update database
            const updatedPreferences: UserPreferences = {
                canvas: preferences.canvas,
                display: {
                    showVoiceButton: preferences.display.showVoiceButton,
                    theme: newTheme,
                },
            };
            updatePreferences(updatedPreferences);
        } else {
            // For unauthenticated users, just toggle locally (temporary)
            toggleLocal();
        }
    };

    // Database-aware theme setter
    const setThemeAndSave = (newTheme: Theme) => {
        // Update theme context immediately
        setTheme(newTheme);

        // Save to database if authenticated
        if (user && preferences) {
            const updatedPreferences: UserPreferences = {
                canvas: preferences.canvas,
                display: {
                    showVoiceButton: preferences.display.showVoiceButton,
                    theme: newTheme,
                },
            };
            updatePreferences(updatedPreferences);
        }
    };

    return {
        toggleTheme,
        setThemeAndSave,
    };
}
